/**
 * Čitanje iz `localStorage` sa podrazumevanom vrednošću koja se primenjuje
 * PO POLJU.
 *
 * Modul je do sada imao samo zapisivanje, pa je svako od sedamnaest čitanja
 * izmislilo svoje podrazumevanje — i svako izmislilo isto pogrešno:
 *
 *     JSON.parse(localStorage.getItem("selected")) || { selectedAccount: "" }
 *
 * Grana `||` gleda CEO objekat. Zapis kome fali samo jedno polje prolazi kao
 * ispravan, pa podrazumevana vrednost tog polja nikad ne stigne. A takav zapis
 * nastaje lako, jer `JSON.stringify` **izbacuje polja sa `undefined`** — jedan
 * `selectedAccount: undefined` i polja više nema, trajno, i preko osvežavanja
 * stranice. Tako je 25.08. izbor baze nestao i uvoz je padao na
 * „Missing server_url, database or composition".
 *
 * Ovde se, kad je podrazumevana vrednost običan objekat, spajaju polje po polje.
 * Za nizove, skalare i `null` vraća se zapisana vrednost ili podrazumevana.
 *
 * Neupotrebljiv zapis se ne propušta dalje: `JSON.parse` nad iskvarenim tekstom
 * baca, a ta čitanja stoje u telu kontekst-providera — dakle bela stranica.
 *
 * @template T
 * @param {string} key
 * @param {T} fallback oblik i podrazumevane vrednosti
 * @returns {T}
 */
export const getFromLocalStorage = (key, fallback) => {
    if (typeof key !== "string" || key === '') {
        throw new Error('Invalid key: must be a non-empty string');
    }
    let stored;
    try {
        const raw = localStorage.getItem(key);
        if (raw === null) return fallback;
        stored = JSON.parse(raw);
    } catch (err) {
        console.error(`Unreadable value in key "${key}", using the default: ${err.message}`);
        return fallback;
    }

    const isPlainObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
    if (!isPlainObject(fallback)) {
        return stored === undefined || stored === null ? fallback : stored;
    }
    if (!isPlainObject(stored)) return fallback;

    // Polje po polje: zapisano ima prednost, ali samo ako ga zaista ima.
    const merged = { ...fallback };
    for (const field of Object.keys(fallback)) {
        if (stored[field] !== undefined && stored[field] !== null) merged[field] = stored[field];
    }
    // Polja koja podrazumevani oblik ne poznaje se zadržavaju — ovaj modul ne
    // zna sve oblike u aplikaciji i nije mu posao da ih čisti.
    for (const field of Object.keys(stored)) {
        if (!(field in merged)) merged[field] = stored[field];
    }
    return merged;
};

export const setToLocalStorage = (key, value) => {
    if (typeof key !== "string" || key === '') {
        throw new Error('Invalid key: must be a non-empty string');
    }
    try {
        const stringValue = JSON.stringify(value);
        localStorage.setItem(key, stringValue);
        return true; // Successfully set in localStorage
    } catch (err) {
        // Check for quota error by multiple possible conditions
        if (
            (err instanceof DOMException && 
                (err.name === 'QuotaExceededError' || err.code === 22)) ||
            err.message?.includes('quota') || 
            err.message?.includes('exceed')
        ) {
            console.error('Local storage is full or quota exceeded.');
        } else {
            console.error(`Error setting value "${value}" in key "${key}": ${err.message}`);
        }
        return false; // Indicate failure
    }
};

export const removeFromLocalStorage = (key) => {
    if (typeof key !== "string" || key === '') {
        throw new Error('Invalid key: must be a non-empty string');
    }
    try {
        localStorage.removeItem(key);
        return true; // Return true to indicate success
    } catch (err) {
        console.error(`Error removing "${key}": ${err.message}`);
        return false; // Return false to indicate failure
    }
};

export const clearLocalStorage = () => {
    try {
        localStorage.clear();
        return true; // Return true to indicate success
    } catch (err) {
        console.error(`Error clearing localStorage ${err.message}`);
        return false; // Return false to indicate failure
    }
}